import { useCallback } from 'react';
import { useDataProvider } from 'react-admin';

export type BatchAssignField = 'designation_id' | 'locale_id' | 'update_channel_id' | 'partner_id';

export interface BatchAssignUnit {
  id: number;
  serial?: string | null;
  hostname?: string | null;
  hardware_revision_id?: number;
  update_channel_id?: number | null;
}

export interface UnitAssignSuccess {
  id: number;
  serial: string | null;
  hostname: string | null;
}

export interface UnitAssignFailure {
  id: number;
  serial: string | null;
  hostname: string | null;
  error: string;
}

export interface BatchAssignResult {
  succeeded: UnitAssignSuccess[];
  failed: UnitAssignFailure[];
  successCount: number;
  failureCount: number;
  totalCount: number;
}

export interface UseBatchAssignUnits {
  batchAssign: (units: BatchAssignUnit[], field: BatchAssignField, value: number) => Promise<BatchAssignResult>;
}

export function useBatchAssignUnits(): UseBatchAssignUnits {
  const dataProvider = useDataProvider();

  const batchAssign = useCallback(
    async (units: BatchAssignUnit[], field: BatchAssignField, value: number): Promise<BatchAssignResult> => {
      const results = await Promise.allSettled(
        units.map((unit) =>
          dataProvider.update('units', {
            id: unit.id,
            data: { [field]: value },
            previousData: {},
          }),
        ),
      );

      const succeeded: UnitAssignSuccess[] = [];
      const failed: UnitAssignFailure[] = [];

      results.forEach((result, index) => {
        const unit = units[index];
        if (result.status === 'fulfilled') {
          succeeded.push({
            id: unit.id,
            serial: unit.serial ?? null,
            hostname: unit.hostname ?? null,
          });
        } else {
          const reason = result.reason as { message?: string; body?: { message?: string } };
          const error = reason?.message ?? reason?.body?.message ?? 'Unknown error';
          failed.push({
            id: unit.id,
            serial: unit.serial ?? null,
            hostname: unit.hostname ?? null,
            error,
          });
        }
      });

      return {
        succeeded,
        failed,
        successCount: succeeded.length,
        failureCount: failed.length,
        totalCount: units.length,
      };
    },
    [dataProvider],
  );

  return { batchAssign };
}
